import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { makeStyles } from '@material-ui/styles'
import moment from 'moment'
import { keys, omit, prop } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { generateApiErrorPayload } from 'api-client/helpers'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import PaginatedSelectableCards from 'core/components/PaginatedSelectableCards'
import {
  ProgressDetails,
  ProgressStatus,
  StepProgress,
} from 'core/components/progress/StepProgress'
import SelectableCard from 'core/components/SelectableCard'
import SizeInputWithUnit, { Unit } from 'core/components/SizeInputWithUnit'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import CodeMirror from 'core/components/validatedForm/CodeMirrorField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import Dropdown from 'core/elements/dropdown'
import Radio from 'core/elements/input/Radio'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import {
  minValueValidator,
  nonDecimalValueValidator,
  requiredValidator,
  validIpv4Validator,
  yamlValidator,
} from 'core/utils/fieldValidators'
import { isHotpluggableFlavor, isOpenCloud } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { isSelfServiceRole } from 'k8s/util/helpers'
import { deleteImage, getImagesWithName } from 'openstack/components/images/actions'
import { waitSeconds } from 'openstack/components/infrastructure/blueprint/helpers'
import { listSshKeys } from 'openstack/components/security/keys/actions'
import { sshKeysSelector } from 'openstack/components/security/keys/selectors'
import { humanReadableSize } from 'openstack/helpers'
import { noop, stopPropagation } from 'utils/fp'
import ServiceUnhealthyInfo from '../common/ServiceUnhealthyInfo'
import { listFlavors } from '../flavors/actions'
import { flavorsSelector } from '../flavors/selectors'
import { ImagePageTabs } from '../images/model'
import ClusterHostGroupsPicklist from '../infrastructure/clusters/ClusterHostGroupsPicklist'
import { listNetworks } from '../networks/actions'
import {
  MANAGED_NETWORK_INFO,
  NetworkMode,
  SIMPLE_NETWORK_TOOLTIP_INFO,
} from '../networks/constants'
import { SharedWithTenants } from '../networks/NetworksListPage'
import { networksSelector } from '../networks/selectors'
import { getVolumeSnapshot } from '../storage/snapshots/actions'
import { createVirtualMachine, getVirtualMachine, snapshotVirtualMachine } from './actions'
import {
  ErrorText,
  getPrivateIpValidator,
  hotplugMemoryMiBValidator,
  hotplugVcpuMaxValidator,
  hotplugVcpuValidator,
  placeholderCloudInitYaml,
} from './CreateVirtualMachineModal'
import { syncNetworkAttachments } from './CreateVirtualMachineModal.helpers'
import {
  getManagedNetworkColumns,
  getManagedNetworkRowExtension,
  getSimpleNetworkColumns,
} from './columns/networkColumns'
import { formatTimestampToLocal, isHotplugVm, isVolumeBackedVm } from './helpers'
import useNetworkPorts, { NetworkPortsResult } from './hooks/useNetworkPorts'
import SshKeyPicklist from './SshKeyPicklist'
import {
  buildNetworkEntries,
  LAYER_2_PORT_METADATA_KEY,
  MANAGED_NETWORK_PORT_METADATA_KEY,
} from './utils/vmBodyBuilder'
import VmCredentialsPanel from './VmCredentialsPanel'
import VmSnapshotInfo from './VmSnapshotInfo'

const ramOptions = [
  { label: 'MiB', value: 'MiB' as Unit },
  { label: 'GiB', value: 'GiB' as Unit },
]

const useStyles = makeStyles<Theme>((theme) => ({
  pairs: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    justifyContent: 'space-between',
  },
  // The fixed heights here are a workaround to keep the flavor cards a uniform size.
  card: {
    height: 192,
    '& .card-body': {
      display: 'grid',
      height: 160,
    },
  },
  cardHeader: {
    fontSize: 20,
  },
  cardText: {
    fontSize: 16,
  },
  successIcon: {
    color: theme.components.badge.success.color,
    marginRight: 8,
  },
  pendingIcon: {
    color: theme.components.badge.warning.color,
    marginRight: 8,
  },
  failedIcon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
  passwordScreen: {
    display: 'grid',
    gap: 24,
  },
  errorMessage: {
    display: 'grid',
    gridTemplateColumns: '26px auto',
    alignItems: 'center',
  },
  errorText: {
    color: theme.components.graph.error,
  },
  portSelection: {
    display: 'grid',
    gridTemplateColumns: '240px auto',
  },
  fixedIPInput: {
    gap: 0,
  },
  cloneProgress: {
    display: 'grid',
    gap: 16,
    padding: 16,
  },
  networkModeCards: {
    display: 'flex',
    gap: theme.spacing(4),
    marginBottom: 16,
  },
  networkCardWrapper: {
    position: 'relative',
    width: '100%',
  },
  networkCard: {
    width: '100%',
    height: '100%',
  },
  networkCardBody: {
    display: 'grid',
    gridGap: theme.spacing(1),
    gridTemplateColumns: 'max-content 1fr max-content',
    padding: '16px',
    placeItems: 'center',
  },
  networkCardContent: {
    justifySelf: 'left',
  },
  networkInfoIcon: {
    color: theme.palette.grey[500],
    fontSize: '20px',
    cursor: 'help',
  },
  networkTooltipBody: {
    maxWidth: 250,
  },
  progressTable: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content max-content',
    gap: 16,
    padding: `0px 16px`,
    alignItems: 'center',
  },
  text: {
    marginBottom: 16,
  },
  centered: {
    textAlign: 'center',
  },
  errorMessageContainer: {
    marginLeft: theme.spacing(1),
  },
}))

interface Props {
  cloneRoute: Route
}

const SubnetsCellComponent = ({ value }) => {
  return (
    <>
      {value.map((subnet) => (
        <Text key={subnet.name} variant="body2">
          {subnet.name}: <b>{subnet.cidr}</b>
        </Text>
      ))}
    </>
  )
}

// Runs the whole clone, step by step: snapshot the source VM, wait for that snapshot image to be
// ready, create the network ports the new VM needs, create the new VM, then clean up the snapshot.
// It reports progress through the setter callbacks and returns true only if the new VM was created.
const cloneVmFromImage = async ({
  vm,
  snapshotFn,
  createFn,
  deleteFn, // action that deletes the snapshot image
  setCloneProgressDetails,
  setVmPassword,
  setSnapshotDeleteError, // shows an error if cleaning up the snapshot fails
  setPortCreationError, // shows an error if creating network ports fails
  params,
  preCreatePortsFn, // creates network ports up front (for both simple and managed networks)
  cleanupPortsFn, // deletes those pre-created ports if something goes wrong
  currentTenant, // the project (tenant) the new VM belongs to
}) => {
  const snapshotName = `${vm?.name}-${moment().format('YYYY-MM-DD-HH:mm:ss')}`
  const snapshotBody = { createImage: { name: snapshotName } }
  const { success: snapshotVmSuccess } = await snapshotFn({ id: vm.id, body: snapshotBody })
  if (!snapshotVmSuccess) {
    setCloneProgressDetails([
      { step: 1, status: 'failed', label: 'VM Snapshot Initiated' },
      { step: 2, status: 'failed', label: 'VM Snapshot Ready' },
      { step: 3, status: 'failed', label: 'VM Creation Initiated' },
      { step: 4, status: 'failed', label: 'VM Snapshot Cleanup' },
    ])
    return false
  }

  setCloneProgressDetails([
    { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
    { step: 2, status: 'pending', label: 'VM Snapshot Ready' },
    { step: 3, status: 'pending', label: 'VM Creation Initiated' },
    { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
  ])

  // Keep checking until the snapshot image shows up and finishes processing. A snapshot becomes a
  // new image in the image library, and we can't build the new VM from it until it's "active".
  let snapshotId = ''
  let snapshotReady = false
  while (!snapshotReady) {
    const images = await getImagesWithName(snapshotName)
    const image = images?.[0]
    if (!image) {
      // snapshot not visible yet, keep waiting
      await waitSeconds(5)
      continue
    }
    snapshotId = image?.id

    if (image?.status === 'active') {
      // If the source VM boots from a volume (not a local disk), the snapshot image also points to
      // one or more volume snapshots. Those have to finish too, so wait until every one of them is
      // "available" before treating the snapshot as ready.
      if (image?.block_device_mapping) {
        try {
          const blockStorage = image.block_device_mapping
            ? JSON.parse(image.block_device_mapping)
            : null
          if (blockStorage) {
            const snapshotIds = blockStorage
              .filter((vol) => vol.source_type === 'snapshot')
              .map((vol) => vol.snapshot_id)
            const promises = snapshotIds?.map((snapshotId) => getVolumeSnapshot(snapshotId))
            const responses = await Promise.allSettled(promises)
            const allSnapshotsAvailable = responses?.every(
              (response: any) => response?.value?.status === 'available',
            )
            if (!allSnapshotsAvailable) {
              await waitSeconds(5)
              continue
            }
          }
        } catch (e) {
          console.error(e)
        }
      }
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'pending', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
      snapshotReady = true
    } else if (['queued', 'saving'].includes(image?.status)) {
      // Snapshot is still being created — wait and check again.
      await waitSeconds(5)
    } else {
      // Any other status (killed, error, deleted, ...) means the snapshot failed. Give up.
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'failed', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'failed', label: 'VM Snapshot Cleanup' },
      ])
      return false
    }
  }

  // Only managed-network rows with a chosen subnet need a port created by us before the VM is
  // built (so it can be pinned to that subnet). Simple (L2) networks are not pre-created — Nova
  // creates and owns their ports from `{ uuid }`.
  let allPreCreatedPorts: NetworkPortsResult = {}
  const hasManagedSubnetSelections = Object.values(params.networkAttachments ?? {}).some((rows) =>
    (rows as any[]).some((row) => row.subnet !== '' && row.port === ''),
  )

  if (hasManagedSubnetSelections) {
    try {
      allPreCreatedPorts = (await preCreatePortsFn(
        params.networks,
        params.networkAttachments ?? {},
        currentTenant,
        params.name,
        1, // Single VM clone
        // Clone has no security-group picker; pre-created ports keep the project default SG
        // (unchanged behavior — server-level SGs from the source VM don't apply to them anyway).
        [],
      )) as NetworkPortsResult
      console.log('[Clone] Network ports created:', allPreCreatedPorts)
    } catch (error) {
      console.error('[Clone] Network port pre-creation failed:', error)
      // Pull a readable message out of the API error so we can show it to the user.
      const errorPayload = generateApiErrorPayload(error, '', 'Failed to create network ports')
      const neutronError =
        (error as any)?.response?.data?.NeutronError || (error as any)?.NeutronError
      const errorTitle = neutronError?.type || 'Port pre-creation failed'

      setPortCreationError({
        title: errorTitle,
        message: errorPayload?.message || 'Failed to create network ports',
      })
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
      return false
    }
  }

  // Build the request body for the new VM. First turn the metadata key/value list into an object.
  const metadata = params.metadata.reduce(
    (accum, pair) => ({ ...accum, [pair.key]: pair.value }),
    {},
  )
  const hotplugMetadata = params.enableHotplugging
    ? {
        HOTPLUG_MEMORY: String(params.hotplugMemory),
        HOTPLUG_MEMORY_MAX: String(params.hotplugMemoryMax),
        HOTPLUG_CPU: String(params.hotplugVcpu),
        HOTPLUG_CPU_MAX: String(params.hotplugVcpuMax),
      }
    : {}

  // Save the IDs of the managed ports we created onto the VM's metadata, so we can find and
  // delete them when the VM is deleted. (Simple/L2 ports are created and owned by Nova.)
  const managedPortIds = Object.values(allPreCreatedPorts).join(',') || undefined

  const combinedMetadata = {
    ...metadata,
    ...hotplugMetadata,
    ...(managedPortIds && { [MANAGED_NETWORK_PORT_METADATA_KEY]: managedPortIds }),
  }

  // Check if config_drive should be auto-enabled
  // Config drive is needed when cloud-init is enabled AND Simple Networks are used
  // (Simple Networks don't have DHCP, so cloud-init needs config_drive to work)
  const shouldEnableConfigDrive =
    params.useCloudInit &&
    params.networks.some(
      (network) =>
        network.isSimpleNetwork ||
        network.subnetDetails?.some((subnet: any) => subnet.enable_dhcp === false),
    )

  // cloud-init is a startup script for the VM. Nova expects it base64-encoded, so encode it here.
  const encodedScript = params.useCloudInit ? btoa(params.cloudInitYaml) : undefined

  const vmBody = {
    server: {
      imageRef: snapshotId,
      flavorRef: params.flavor?.id,
      name: params.name,
      // PCD-5420: turn the network attachment rows into the list of NICs Nova should attach — one
      // entry per row. This is the same builder the Create VM flow uses.
      networks: buildNetworkEntries(params, {
        managedNetworkPorts: allPreCreatedPorts,
      }),
      key_name: params?.sshKey || undefined,
      config_drive: shouldEnableConfigDrive,
      user_data: params.useCloudInit ? encodedScript : undefined,
      security_groups: vm?.security_groups,
      metadata: combinedMetadata,
      ...(params.availabilityZone && { availability_zone: params.availabilityZone }),
      // availability_zone: vm?.['OS-EXT-AZ:availability_zone']
      //   ? vm?.['OS-EXT-AZ:availability_zone']
      //   : undefined,
    },
    // 'OS-SCH-HNT:scheduler_hints': vm?.serverGroup
    //   ? {
    //       group: vm?.serverGroup?.id,
    //     }
    //   : undefined,
  }

  // Create the new VM, then keep checking on it until it has finished building.
  let vmInfo
  let createSuccessFlag = false
  try {
    const { success: createSuccess, response } = await createFn({ body: vmBody })
    createSuccessFlag = createSuccess
    vmInfo = response
    if (createSuccess) {
      // Wait while the VM is still building (or while we don't have its details yet — the list API
      // may not return a brand-new VM immediately). Re-fetch every few seconds until it's ready.
      while (!vmInfo?.['OS-EXT-STS:vm_state'] || vmInfo['OS-EXT-STS:vm_state'] === 'building') {
        setCloneProgressDetails([
          { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
          { step: 2, status: 'success', label: 'VM Snapshot Ready' },
          { step: 3, status: 'pending', label: 'VM Creation Initiated' },
          { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
        ])
        await waitSeconds(5)
        try {
          const result = await getVirtualMachine({ id: response.id })
          if (result) vmInfo = result
        } catch (err) {
          console.error('VM polling failed:', err)
          // One failed check is fine — don't stop, just loop around and try again.
        }
      }

      // VM finished building. Mark step 3 done and hand back the auto-generated admin password.
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'success', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])

      setVmPassword(response?.adminPass)
    } else {
      // VM creation failed
      setCloneProgressDetails([
        { step: 1, status: 'success', label: 'VM Snapshot Initiated' },
        { step: 2, status: 'success', label: 'VM Snapshot Ready' },
        { step: 3, status: 'failed', label: 'VM Creation Initiated' },
        { step: 4, status: 'pending', label: 'VM Snapshot Cleanup' },
      ])
    }
  } finally {
    // This block always runs, whether the VM was created or not.
    // First: if the VM failed but we already created ports for it, delete those ports so they
    // don't get orphaned.
    const hasPreCreatedPorts = Object.keys(allPreCreatedPorts).length > 0
    if (!createSuccessFlag && hasPreCreatedPorts) {
      try {
        console.log('[Clone] Cleaning up pre-created ports due to failure...')
        await cleanupPortsFn(allPreCreatedPorts)
      } catch (cleanupError) {
        console.error('[Clone] Port cleanup failed:', cleanupError)
      }
    }

    // Then: always try to delete the temporary snapshot image. It was only needed to build the
    // new VM, so we remove it whether the clone succeeded or failed.
    try {
      const { success: deleteSuccess, error } = await deleteFn({
        id: snapshotId,
      })
      setCloneProgressDetails((prev) => [
        ...prev.slice(0, 3),
        {
          step: 4,
          status: deleteSuccess ? 'success' : 'failed',
          label: 'VM Snapshot Cleanup',
        },
      ])
      if (!deleteSuccess) throw error || new Error('VM Snapshot Cleanup failed')
    } catch (err) {
      setSnapshotDeleteError(err)
    }
  }

  return createSuccessFlag
}

export default function CloneVirtualMachineModal({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const [cloningInProgress, setCloningInProgress] = useState(false)
  const [showCloneProgress, setShowCloneProgress] = useState(false)
  const [cloneProgressDetails, setCloneProgressDetails] = useState<ProgressDetails[]>([
    { step: 1, status: ProgressStatus.Pending, label: 'VM Snapshot Initiated' },
    { step: 2, status: ProgressStatus.Pending, label: 'VM Snapshot Ready' },
    { step: 3, status: ProgressStatus.Pending, label: 'VM Creation Initiated' },
    { step: 4, status: ProgressStatus.Pending, label: 'VM Snapshot Cleanup' },
  ])
  const [loadedDefaultFlavor, setLoadedDefaultFlavor] = useState(false)
  const [defaultFlavor, setDefaultFlavor] = useState(null)
  const [loadedDefaultNetworks, setLoadedDefaultNetworks] = useState(false)
  const [loadedDefaultSshKey, setLoadedDefaultSshKey] = useState(false)
  const [showVmPassword, setShowVmPassword] = useState(false)
  const [vmPassword, setVmPassword] = useState('')
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [vmNetworkMode, setVmNetworkMode] = useState<NetworkMode | null>(null)
  const isHotPluggedVm = isHotplugVm(vm)
  const defaultParams = {
    name: `${vm?.name}-clone`,
    enableHotplugging: isHotPluggedVm,
    image: null,
    flavor: null,
    selectedPorts: {},
    fixedIPs: {},
    selectedSubnets: {},
    // PCD-5420: per managed network, the list of NIC rows to attach (one row = one NIC). This is
    // what lets a clone use multiple subnets / multiple ports, using the same model as Create VM.
    networkAttachments: {},
    networks: [],
    availabilityZone: vm?.['OS-EXT-AZ:availability_zone'],
    sshKey: null,
    useCloudInit: false,
    cloudInitYaml: placeholderCloudInitYaml,
    hotplugMemory: vm?.metadata?.['HOTPLUG_MEMORY'] ? Number(vm.metadata['HOTPLUG_MEMORY']) : null,
    // Legacy sources without *_MAX metadata get the default maximum, clamped up to the source's
    // current allocation so the prefilled form is never in a current-exceeds-max invalid state.
    hotplugMemoryMax: vm?.metadata?.['HOTPLUG_MEMORY_MAX']
      ? Number(vm.metadata['HOTPLUG_MEMORY_MAX'])
      : Math.max(65536, Number(vm?.metadata?.['HOTPLUG_MEMORY'] ?? 0)),
    hotplugVcpu: vm?.metadata?.['HOTPLUG_CPU'] ? vm.metadata['HOTPLUG_CPU'] : null,
    hotplugVcpuMax: vm?.metadata?.['HOTPLUG_CPU_MAX']
      ? Number(vm.metadata['HOTPLUG_CPU_MAX'])
      : Math.max(64, Number(vm?.metadata?.['HOTPLUG_CPU'] ?? 0)),
    metadata: vm?.metadata
      ? Object.entries(
          omit(
            [
              'HOTPLUG_MEMORY',
              'HOTPLUG_MEMORY_MAX',
              'HOTPLUG_CPU',
              'HOTPLUG_CPU_MAX',
              LAYER_2_PORT_METADATA_KEY, // Exclude original VM's pre-created port IDs
              MANAGED_NETWORK_PORT_METADATA_KEY,
            ],
            vm.metadata,
          ),
        ).map(([key, value]) => {
          return { key, value }
        })
      : [],
  }

  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const isSelfServiceUser = isSelfServiceRole(session)

  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingFlavors } = useListAction(listFlavors, {
    initialLoadingState: true,
  })
  const flavors = useSelectorWithParams(flavorsSelector, {} as any)

  const { loading: loadingNetworks } = useListAction(listNetworks, {
    initialLoadingState: true,
  })
  const _networksList = useSelectorWithParams(networksSelector, {} as any)
  const networksList = _networksList.filter((network) => {
    // Include Simple Networks (L2-only, no subnets) AND Managed Networks (with subnets)
    const isManagedNetwork = network.subnets?.length > 0
    const isSimpleNet = network.isSimpleNetwork

    // Must be either Managed or Simple network
    if (!isManagedNetwork && !isSimpleNet) return false

    // If network is shared, show it
    if (network.shared) return true

    // For networks in current tenant
    if (network.tenant_id === currentTenant) {
      // For self service users, hide external networks
      if (isSelfServiceUser) return !network?.['router:external']
      // For non-self service users, show all networks
      return true
    }

    // Hide networks not in current tenant
    return false
  })

  // Calculate network counts for toggle cards
  const managedNetworksCount = useMemo(() => {
    return networksList.filter((network) => !network.isSimpleNetwork && network.subnets?.length > 0)
      .length
  }, [networksList])

  const simpleNetworksCount = useMemo(() => {
    return networksList.filter((network) => network.isSimpleNetwork).length
  }, [networksList])

  const { loading: loadingSshKeys } = useListAction(listSshKeys, {
    initialLoadingState: true,
  })
  const sshKeys = useSelector(sshKeysSelector)

  const { update: snapshotVm } = useUpdateAction(snapshotVirtualMachine)
  const { update: createVm, updating, error, reset } = useUpdateAction(createVirtualMachine)
  const { update: deleteSnapshotFn } = useUpdateAction(deleteImage)
  const [snapshotDeleteError, setSnapshotDeleteError] = useState(null)
  const [portCreationError, setPortCreationError] = useState<{
    title: string
    message: string
  } | null>(null)
  const responseError = error

  // Network port management hook (handles both simple and managed networks)
  const { preCreatePorts, cleanupPreCreatedPorts } = useNetworkPorts()

  const submitForm = useCallback(async () => {
    setAttemptedSubmit(true)
    setShowCloneProgress(true)
    setCloningInProgress(true)
    const cloneSuccess = await cloneVmFromImage({
      vm,
      snapshotFn: snapshotVm,
      createFn: createVm,
      deleteFn: deleteSnapshotFn,
      setCloneProgressDetails,
      setVmPassword,
      setSnapshotDeleteError,
      setPortCreationError,
      params: params,
      preCreatePortsFn: preCreatePorts,
      cleanupPortsFn: cleanupPreCreatedPorts,
      currentTenant,
    })
    setCloningInProgress(false)
    if (cloneSuccess) {
      setShowCloneProgress(false)
      setShowVmPassword(true)
    }
  }, [params, vm, preCreatePorts, cleanupPreCreatedPorts, currentTenant])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    setSnapshotDeleteError(null)
    setPortCreationError(null)
    setShowCloneProgress(false)
    setShowVmPassword(false)
    setVmPassword('')
    reset()
    onClose(success)
  }

  useEffect(() => {
    if (loadingFlavors || loadedDefaultFlavor) {
      return
    }
    const vmFlavor = vm?.flavor
    const defaultFlavor = flavors.find((flavor) => {
      return (
        vmFlavor?.original_name === flavor.name &&
        vmFlavor?.disk === flavor.disk &&
        // For hotplugged VMs, we don't want to match the RAM and vCPUs, as flavors will always show 0 vCPUs and 0 RAM
        (!isHotPluggedVm ? vmFlavor?.ram === flavor.ram && vmFlavor?.vcpus === flavor.vcpus : true)
      )
    })

    updateParams({ flavor: defaultFlavor })
    setDefaultFlavor(defaultFlavor)
    setLoadedDefaultFlavor(true)
  }, [loadingFlavors, flavors, vm])

  // Flag to determine if VM is volume backed
  const isVolumeBackedVM = useMemo(() => isVolumeBackedVm(vm), [vm])

  // Want to show the default selected flavor as the first on the list
  const orderedFlavors = useMemo(() => {
    // if hotplugging is enabled, only show flavors with 0 vcpus and 0 ram
    if (params?.enableHotplugging) {
      const hotpluggableFlavors = flavors.filter((flavor) => {
        return isHotpluggableFlavor(flavor)
      })
      updateParams({
        flavor: isHotPluggedVm ? defaultFlavor : hotpluggableFlavors[0],
      })
      return hotpluggableFlavors
    }

    if (defaultFlavor) {
      const restFlavors = flavors.filter(
        (flavor) =>
          flavor.id !== defaultFlavor.id &&
          !isHotpluggableFlavor(flavor) &&
          (isVolumeBackedVM ? flavor?.disk === 0 : flavor?.disk !== 0),
      )
      updateParams({
        flavor: !isHotPluggedVm ? defaultFlavor : restFlavors[0],
      })
      return [...(!isHotPluggedVm ? [defaultFlavor] : []), ...restFlavors]
    } else {
      const restFlavors = flavors.filter(
        (flavor) =>
          !isHotpluggableFlavor(flavor) &&
          (isVolumeBackedVM ? flavor?.disk === 0 : flavor?.disk !== 0),
      )
      updateParams({ flavor: restFlavors[0] })
      return restFlavors
    }
  }, [flavors, defaultFlavor, params?.enableHotplugging, isHotPluggedVm, isVolumeBackedVM])

  useEffect(() => {
    if (loadingNetworks || loadedDefaultNetworks) {
      return
    }
    const vmNetworkNames = keys(vm?.addresses)
    const defaultNetworks = networksList.filter((network) => {
      return vmNetworkNames.includes(network.name)
    })

    // Detect network mode based on default networks
    if (defaultNetworks.length > 0) {
      const hasSimpleNetwork = defaultNetworks.some((network) => network.isSimpleNetwork)
      const hasManagedNetwork = defaultNetworks.some(
        (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
      )

      // Set network mode based on VM's current networks
      if (hasSimpleNetwork && !hasManagedNetwork) {
        setVmNetworkMode(NetworkMode.SIMPLE)
      } else if (hasManagedNetwork && !hasSimpleNetwork) {
        setVmNetworkMode(NetworkMode.MANAGED)
      } else {
        // If mixed or no networks, default to Managed
        setVmNetworkMode(NetworkMode.MANAGED)
      }
    } else {
      // Default to Managed if no networks
      setVmNetworkMode(NetworkMode.MANAGED)
    }

    updateParams({
      networks: defaultNetworks,
      // PCD-5420: give each pre-selected network one starting NIC row (Automatic subnet and port).
      networkAttachments: syncNetworkAttachments(defaultNetworks, {}),
    })
    setLoadedDefaultNetworks(true)
  }, [loadingNetworks, networksList, vm])

  useEffect(() => {
    if (loadingSshKeys || loadedDefaultSshKey) {
      return
    }
    // If the source VM's SSH key still exists, pre-select it for the clone.
    const sshKeyPresent = sshKeys.some((key) => key.name === vm?.key_name)
    if (sshKeyPresent) {
      updateParams({ sshKey: vm.key_name })
    }
    setLoadedDefaultSshKey(true)
  }, [loadingSshKeys, sshKeys, vm])

  const renderFlavorCard = useCallback(
    (item) => {
      // A flavor's disk must be at least as big as the source image, otherwise it can't fit the
      // VM's disk and we disable the card. (A flavor with 0 disk is volume-backed, so it's allowed.)
      const imageSize = vm?.imageDetails?.virtual_size
      const flavorSize = item.disk * 1024 * 1024 * 1024
      const imageValid = flavorSize === 0 ? true : imageSize ? flavorSize >= imageSize : true
      const disabled = !imageValid
      return (
        <SelectableCard
          id={item}
          onClick={(item) => updateParams({ flavor: item })}
          active={params.flavor?.id === item.id}
          className={classes.card}
          disabled={disabled}
          showCheckmarkIcon
        >
          <Text variant="body2" className={classes.cardHeader}>
            <b>{item.name}</b>
          </Text>
          <br />
          <div className={classes.pairs}>
            <Text variant="body2" className={classes.cardText}>
              vCPUs:
            </Text>
            <Text variant="body2" className={classes.cardText}>
              <b>{item?.vcpus}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              RAM:
            </Text>
            <Text variant="body2" className={classes.cardText}>
              <b>{item?.ram && humanReadableSize(item?.ram * 1024 * 1024)}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Disk:
            </Text>
            <Text variant="body2" className={classes.cardText}>
              <b>{item?.disk && humanReadableSize(item?.disk * 1024 * 1024 * 1024)}</b>
              {disabled && ' (Too Small)'}
            </Text>
          </div>
        </SelectableCard>
      )
    },
    [vm, params.flavor],
  )

  // The table columns for each network mode. The definitions live in ./columns/networkColumns.
  // Clone keeps the read-only "Automatic" L2 Ports column (L2 port selection is Create-only).
  const simpleNetworkColumns = useMemo(() => getSimpleNetworkColumns(), [])

  const managedNetworkColumns = useMemo(
    () => getManagedNetworkColumns({ params, getParamsUpdater }),
    [params.selectedPorts, params.fixedIPs, params.selectedSubnets, params.networkAttachments],
  )

  // PCD-5420: the expandable area under each managed network row — it holds the extra "+ Add
  // subnet" sub-rows. (For simple networks this isn't used; we only pass it in Managed mode below.)
  const managedRowExtension = useMemo(
    () => getManagedNetworkRowExtension({ params, getParamsUpdater }),
    [params.networkAttachments],
  )

  // Select columns based on network mode
  const networkColumns = useMemo(() => {
    return vmNetworkMode === NetworkMode.SIMPLE ? simpleNetworkColumns : managedNetworkColumns
  }, [vmNetworkMode, simpleNetworkColumns, managedNetworkColumns])

  // Filter network list based on selected mode
  const filteredNetworksList = useMemo(() => {
    if (!vmNetworkMode) return []
    if (vmNetworkMode === NetworkMode.SIMPLE) {
      return networksList.filter((network) => network.isSimpleNetwork)
    } else if (vmNetworkMode === NetworkMode.MANAGED) {
      return networksList.filter(
        (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
      )
    }
    return networksList
  }, [vmNetworkMode, networksList])

  // Old network table column definitions, no longer used (replaced by the mode-aware columns
  // above). Kept here for reference only — the underscore prefix marks it as unused.
  const _legacyNetworkColumns = [
    {
      id: 'name',
      label: 'Name',
    },
    {
      id: 'id',
      label: 'UUID',
      display: false,
    },
    {
      id: 'subnetDetails',
      label: 'Subnets',
      render: (value) => <SubnetsCellComponent value={value} />,
    },
    {
      id: 'ports',
      label: 'Ports',
      Component: ({ row, isSelected }) => {
        const classes = useStyles()
        // Filter out ports that are already bound to a device
        const filteredPorts = row?.ports?.filter(
          (port) =>
            port['binding:vif_type'] === 'unbound' &&
            (port?.device_owner.trim() === '' || !port?.device_owner),
        )

        const options = filteredPorts?.length
          ? [
              { label: 'Automatic', value: '' },
              { label: 'Private IP', value: 'fixedIP' },
              ...filteredPorts.map((port) => {
                const ipAddressString = port.fixed_ips?.map((ip) => ip.ip_address)?.join(', ')
                return {
                  label: `${ipAddressString} (${port.name || port.id})`,
                  value: port.id,
                }
              }),
            ]
          : [
              { label: 'Automatic', value: '' },
              { label: 'Private IP', value: 'fixedIP' },
            ]
        return (
          <div className={classes.portSelection} onClick={isSelected ? stopPropagation : noop}>
            <Dropdown
              name="ports"
              value={params.selectedPorts[row.id] || ''}
              items={options}
              onChange={(value) =>
                getParamsUpdater('selectedPorts')({
                  ...params.selectedPorts,
                  [row.id]: value,
                })
              }
            ></Dropdown>
          </div>
        )
      },
    },
    {
      id: 'fixedIP',
      label: 'Private IP',
      Component: ({ row, isSelected }) => {
        const [fixedIP, setFixedIP] = useState(params.fixedIPs[row.id])
        const isRequired = params.selectedPorts[row.id] === 'fixedIP'
        const selectedPort = row?.ports?.find((port) => port.id === params.selectedPorts[row.id])
        return isRequired ? (
          <div onClick={isSelected ? stopPropagation : noop}>
            <TextField
              id={`${row.id}-fixedIP`}
              placeholder="Enter private IP"
              className={classes.fixedIPInput}
              value={fixedIP}
              onChange={setFixedIP}
              onBlur={() => {
                getParamsUpdater('fixedIPs')({ ...params.fixedIPs, [row.id]: fixedIP })
              }}
              disabled={!isRequired}
              validations={
                isRequired && isSelected
                  ? [validIpv4Validator, requiredValidator, getPrivateIpValidator(row)]
                  : null
              }
            />
          </div>
        ) : selectedPort ? (
          <div>
            {selectedPort?.fixed_ips?.map((ip) => (
              <Text variant="body2">{ip.ip_address}</Text>
            ))}
          </div>
        ) : null
      },
    },
    {
      id: 'port_security_enabled',
      label: 'Port Security',
      render: (val) => (val ? 'Enabled' : 'Disabled'),
      display: false,
    },
    {
      id: 'provider:network_type',
      label: 'Network Type',
      display: false,
    },
    {
      id: 'mtu',
      label: 'MTU',
      display: false,
    },
    {
      id: 'provider:physical_network',
      label: 'Physical Network',
      display: false,
    },
    {
      id: 'router:external',
      label: 'External',
      render: (val) => (val ? 'Yes' : 'No'),
      display: false,
    },
    {
      id: 'provider:segmentation_id',
      label: 'Segmentation ID',
      render: (val) => (val ? val : 'N/A'),
      display: false,
    },
    {
      id: 'admin_state_up',
      label: 'Admin State',
      render: (val) => (val ? 'Up' : 'Down'),
      display: false,
    },
    {
      id: 'dns_domain',
      label: 'DNS Zone',
      render: (val) => (val ? val : '-'),
      display: false,
    },
    {
      id: 'status',
      label: 'Status',
      display: false,
    },
    {
      id: 'availableIps',
      label: 'Available IPs',
      display: false,
    },
    {
      id: 'shared',
      label: 'Shared Tenants',
      render: (value, item) => <SharedWithTenants item={item} value={value} />,
      display: false,
    },
    {
      id: 'created_at',
      label: 'Created',
      render: (time: string) => formatTimestampToLocal(time),
      display: false,
    },
  ] // End of legacy columns

  return (
    <ModalForm
      title={`Clone Virtual Machine`}
      onSubmit={showCloneProgress || showVmPassword ? handleClose : submitForm}
      disableSubmit={showCloneProgress && cloningInProgress}
      onClose={handleClose}
      // submitting={updating}
      error={responseError}
      submitTitle={showCloneProgress || showVmPassword ? `Finish` : `Clone Virtual Machine`}
      maxWidth={1200}
      open
    >
      {!showCloneProgress && !showVmPassword && (
        <>
          <ServiceUnhealthyInfo action="VM Clone" hypervisor imageLibrary blockStorage />
          <VmSnapshotInfo vm={vm} isClonedVM />
          <Text variant="body2" className={classes.text}>
            Clone this virtual machine. A VM Snapshot will be taken and a new VM will be created
            with identical disks and settings. Public IPs will not be assigned.
          </Text>
          <FormFieldSection title="Confirm or Edit your Flavor" step={1}>
            <CheckboxField
              id="enableHotplugging"
              value={params.enableHotplugging}
              label="Enable Hotplugging"
              onChange={getParamsUpdater('enableHotplugging')}
              info="Enable the ability to hotplug VM memory and CPU. Usage of a flavor set to 0 memory and 0 vCPUs is required."
            />
            <Text variant="body2">
              <b>NOTE</b>: When enabling hotplugging, only flavors with 0 RAM and 0 vCPUs will be
              displayed.
            </Text>
            {attemptedSubmit && !params.flavor && <ErrorText>You must select a flavor</ErrorText>}
            <PaginatedSelectableCards
              renderSelectableCardComponent={renderFlavorCard}
              data={orderedFlavors}
              searchProps={['name']}
            />
          </FormFieldSection>
          <FormFieldSection title="Confirm or Edit your Networks" step={2}>
            {/* Select Network Type */}
            <Text
              variant="body2"
              style={{ marginBottom: '16px', display: 'block', fontWeight: '600' }}
            >
              Select Network Type
            </Text>
            <div className={classes.networkModeCards}>
              <div className={classes.networkCardWrapper}>
                <SelectableCard
                  id="managed"
                  onClick={() => {
                    setVmNetworkMode(NetworkMode.MANAGED)
                    // Switching network type clears any networks (and their per-network choices)
                    // picked under the previous type, since they don't carry over.
                    if (params.networks.length > 0) {
                      getParamsUpdater('networks')([])
                      getParamsUpdater('fixedIPs')({})
                      getParamsUpdater('selectedPorts')({})
                      getParamsUpdater('selectedSubnets')({})
                      getParamsUpdater('networkAttachments')({})
                    }
                  }}
                  active={vmNetworkMode === NetworkMode.MANAGED}
                  className={classes.networkCard}
                  withCustomBody
                >
                  <div className={classes.networkCardBody}>
                    <Radio checked={vmNetworkMode === NetworkMode.MANAGED} />
                    <Text variant="caption1" className={classes.networkCardContent}>
                      Managed Networks ({managedNetworksCount})
                    </Text>
                    <Tooltip
                      customClassName={classes.networkTooltipBody}
                      message={MANAGED_NETWORK_INFO}
                    >
                      <HelpOutlineIcon className={classes.networkInfoIcon} />
                    </Tooltip>
                  </div>
                </SelectableCard>
              </div>
              <div className={classes.networkCardWrapper}>
                <SelectableCard
                  id="simple"
                  onClick={() => {
                    setVmNetworkMode(NetworkMode.SIMPLE)
                    // Switching network type clears any networks (and their per-network choices)
                    // picked under the previous type, since they don't carry over.
                    if (params.networks.length > 0) {
                      getParamsUpdater('networks')([])
                      getParamsUpdater('fixedIPs')({})
                      getParamsUpdater('selectedPorts')({})
                      getParamsUpdater('selectedSubnets')({})
                      getParamsUpdater('networkAttachments')({})
                    }
                  }}
                  active={vmNetworkMode === NetworkMode.SIMPLE}
                  className={classes.networkCard}
                  withCustomBody
                >
                  <div className={classes.networkCardBody}>
                    <Radio checked={vmNetworkMode === NetworkMode.SIMPLE} />
                    <Text variant="caption1" className={classes.networkCardContent}>
                      Layer 2 Networks ({simpleNetworksCount})
                    </Text>
                    <Tooltip
                      customClassName={classes.networkTooltipBody}
                      message={SIMPLE_NETWORK_TOOLTIP_INFO}
                    >
                      <HelpOutlineIcon className={classes.networkInfoIcon} />
                    </Tooltip>
                  </div>
                </SelectableCard>
              </div>
            </div>

            <ListTableField
              id="networks"
              data={filteredNetworksList}
              loading={loadingNetworks}
              columns={networkColumns}
              renderRowExtension={
                vmNetworkMode === NetworkMode.MANAGED ? managedRowExtension : undefined
              }
              onChange={(selectedNetworks) => {
                getParamsUpdater('networks')(selectedNetworks)

                // PCD-5420: keep the per-network NIC rows in step with what's selected — a newly
                // checked network gets one default row, and unchecked networks have their rows dropped.
                getParamsUpdater('networkAttachments')(
                  syncNetworkAttachments(selectedNetworks, params.networkAttachments),
                )

                // Also drop the saved subnet choices for any network that's no longer selected.
                const selectedIds = new Set(selectedNetworks.map((n) => n.id))
                const cleanedSubnets = Object.fromEntries(
                  Object.entries(params.selectedSubnets ?? {}).filter(([id]) =>
                    selectedIds.has(id),
                  ),
                )
                getParamsUpdater('selectedSubnets')(cleanedSubnets)
              }}
              value={params.networks}
              uniqueIdentifier="id"
              searchTargets={['name']}
              multiSelection
              required
            />
          </FormFieldSection>
          <FormFieldSection title="Customize your VM" step={4}>
            <TextField
              id="name"
              label="Name"
              onChange={getParamsUpdater('name')}
              value={params.name}
              info="Choose a name for your VM"
              required
            />
            <PicklistField
              id="availabilityZone"
              label="Cluster"
              info="Choose a cluster for your VM"
              onChange={getParamsUpdater('availabilityZone')}
              value={params.availabilityZone}
              disableClusterWithoutHosts
              fullWidth
              required={isVmware && isOpenCloud() ? false : true}
              DropdownComponent={ClusterHostGroupsPicklist}
            />
            <PicklistField
              id="sshKey"
              DropdownComponent={SshKeyPicklist}
              value={params.sshKey}
              onChange={getParamsUpdater('sshKey')}
              tooltip="Select an SSH key for your VM"
            />
            <CheckboxField
              id="useCloudInit"
              value={params.useCloudInit}
              label="Use cloud-init"
              onChange={getParamsUpdater('useCloudInit')}
            />
            {params.useCloudInit && (
              <CodeMirror
                variant="light"
                label="cloud-init YAML"
                id="cloudInitYaml"
                onChange={getParamsUpdater('cloudInitYaml')}
                value={params.cloudInitYaml}
                // className={fullWidth}
                validations={[yamlValidator]}
              />
            )}
            {params.enableHotplugging && (
              <>
                <SizeInputWithUnit
                  id="hotplugMemory"
                  label="Hotplug RAM"
                  onChange={(val) => updateParams({ hotplugMemory: val })}
                  value={params.hotplugMemory}
                  defaultUnit="MiB"
                  min={1}
                  info="The memory that will be hotplugged to the VM. Must be less than the maximum."
                  validations={[
                    hotplugMemoryMiBValidator(
                      Number(params.hotplugMemory),
                      Number(params.hotplugMemoryMax),
                    ),
                    nonDecimalValueValidator(),
                  ]}
                  required
                  items={ramOptions}
                />
                <SizeInputWithUnit
                  id="hotplugMemoryMax"
                  label="Hotplug RAM Maximum"
                  onChange={(val) => updateParams({ hotplugMemoryMax: val })}
                  value={params.hotplugMemoryMax}
                  defaultUnit="MiB"
                  min={1}
                  info="The maximum amount of RAM that can be hotplugged to the VM."
                  validations={[minValueValidator(1), nonDecimalValueValidator()]}
                  required
                  items={ramOptions}
                />
                <TextField
                  id="hotplugVcpu"
                  label="Hotplug vCPUs"
                  onChange={(val) => updateParams({ hotplugVcpu: val })}
                  value={params.hotplugVcpu}
                  type="number"
                  min={1}
                  info="The number of vCPUs that will be hotplugged to the VM. Must be less than the maximum."
                  validations={[hotplugVcpuValidator, nonDecimalValueValidator()]}
                  required
                />
                <TextField
                  id="hotplugVcpuMax"
                  label="Hotplug vCPUs Maximum"
                  onChange={(val) => updateParams({ hotplugVcpuMax: val })}
                  value={params.hotplugVcpuMax}
                  type="number"
                  min={1}
                  info="The maximum number of vCPUs that can be hotplugged to the VM."
                  validations={[hotplugVcpuMaxValidator, nonDecimalValueValidator()]}
                  required
                />
              </>
            )}
            <KeyValuesField
              id="metadata"
              label="Metadata"
              value={params.metadata}
              onChange={getParamsUpdater('metadata')}
              addLabel="Add Metadata"
              allowMultipleValues
            />
          </FormFieldSection>
        </>
      )}
      {showCloneProgress && (
        <>
          {portCreationError && (
            <Alert
              variant="error"
              title={portCreationError.title}
              message={portCreationError.message}
            />
          )}
          <StepProgress
            progressDetails={cloneProgressDetails}
            title="Cloning is in progress. Please wait and do not close this dialog, or the clone operation will be cancelled."
          />
        </>
      )}
      {showVmPassword && (
        <div className={classes.passwordScreen}>
          {snapshotDeleteError && (
            <Alert
              variant="error"
              title=""
              message={
                <div className={classes.errorMessageContainer}>
                  <ErrorMessage>
                    <Text variant="body2">
                      Failed to cleanup the VM snapshot. Ensure that the image library host is
                      reachable and its certificate is trusted. You may need to delete the VM
                      snapshot manually.{' '}
                      <ExternalLink
                        url={routeManager.routes.openstack.images.path({
                          tab: ImagePageTabs.Snapshots,
                        })}
                      >
                        Click here
                      </ExternalLink>
                    </Text>
                  </ErrorMessage>
                </div>
              }
            />
          )}
          <VmCredentialsPanel vmPassword={vmPassword} />
        </div>
      )}
    </ModalForm>
  )
}
